import { z } from 'zod';

import { enforceRangePairs } from '@/lib/api/filter-spec';
import { pageMetaSchema } from '@/lib/api/pagination';
import { AVAILABLE_COUNT_MAX } from '@/lib/constants';
import { assetToUploadedFile, productAssetSchema } from '@/lib/upload';

export const deviceStatusSchema = z.enum(['active', 'inactive']);
export const deviceTypeSchema = z.enum(['dm_device', 'cropx', 'other']);

/** A single row in the admin devices listing (GET /products/admin/devices). */
export const deviceRowSchema = z.object({
  id: z.string(),
  name: z.string(),
  deviceType: deviceTypeSchema,
  category: z.object({ id: z.string(), name: z.string() }),
  components: z.array(z.object({ id: z.string(), name: z.string() })),
  price: z.number(),
  installationPrice: z.number().nullable(), // Optional since CR 122166 — null = not set.
  availableCount: z.number(),
  warrantyMonths: z.number().nullable(), // Optional since CR 122166 — null = not set.
  // Null for `other` devices (no subscription) — spec D3.
  subscriptionPrice: z.number().nullable(),
  status: deviceStatusSchema,
  createdAt: z.string(),
});

export const deviceListResponseSchema = z.object({
  data: z.array(deviceRowSchema),
  meta: pageMetaSchema,
});

/** Active categories for the filter dropdown (GET /products/admin/meta/categories). */
export const metaCategorySchema = z.object({ id: z.string(), name: z.string() });

/** Selectable components for the Applicable Components filter (GET /products/admin/meta/components).
 *  The backend never includes `status` on these items — it filters server-side instead, via the
 *  `status` query param `listMetaComponents` sends (ADO 117569). */
export const metaComponentSchema = z.object({
  id: z.string(),
  name: z.string(),
});

/**
 * Filter-drawer draft values (story #102080). Empty number inputs are `null`
 * (antd InputNumber clears to null); cleared antd Select → `undefined` (`''` is
 * never a valid value). From≤To is enforced per range pair and the error is
 * attached to the `*To` field.
 */
export const deviceFiltersSchema = z
  .object({
    // `.nullish()`, not `.optional()`: the drawer's clear control writes `null` so the antd
    // select actually re-renders empty (ADO 112241); `toFilters` folds it back to "no filter".
    deviceType: deviceTypeSchema.nullish(),
    status: deviceStatusSchema.nullish(),
    categoryIds: z.array(z.string()),
    componentIds: z.array(z.string()),
    priceFrom: z.number().nullable(),
    priceTo: z.number().nullable(),
    installationFeeFrom: z.number().nullable(),
    installationFeeTo: z.number().nullable(),
    subscriptionPriceFrom: z.number().nullable(),
    subscriptionPriceTo: z.number().nullable(),
    availableFrom: z.number().nullable(),
    availableTo: z.number().nullable(),
    warrantyFrom: z.number().nullable(),
    warrantyTo: z.number().nullable(),
  })
  .superRefine(
    enforceRangePairs([
      ['priceFrom', 'priceTo'],
      ['installationFeeFrom', 'installationFeeTo'],
      ['subscriptionPriceFrom', 'subscriptionPriceTo'],
      ['availableFrom', 'availableTo'],
      ['warrantyFrom', 'warrantyTo'],
    ]),
  )
  // US 102080 (AC updated 2026-09-24, ADO 120964): 0 is not a valid bound for the three price
  // ranges, From or To. Available Devices and Warranty are counts and keep accepting 0.
  .superRefine((v, ctx) => {
    const zeroChecked = [
      ['priceFrom', 'From'],
      ['priceTo', 'To'],
      ['installationFeeFrom', 'From'],
      ['installationFeeTo', 'To'],
      ['subscriptionPriceFrom', 'From'],
      ['subscriptionPriceTo', 'To'],
    ] as const;
    for (const [field, side] of zeroChecked) {
      if (v[field] === 0) {
        ctx.addIssue({ code: 'custom', path: [field], message: `${side} value cannot be 0` });
      }
    }
  });

/** Device detail / create / update response. Uploads come back as asset objects under
 *  `images` / `installationGuides`; `installationGuides` may be null (coerced to `[]`). */
export const deviceSchema = z.object({
  id: z.string(),
  name: z.string(),
  deviceType: deviceTypeSchema,
  availableCount: z.number(),
  categoryId: z.string(),
  warrantyMonths: z.number().nullable(), // Optional since CR 122166 — null = not set.
  price: z.number(),
  installationPrice: z.number().nullable(), // Optional since CR 122166 — null = not set.
  componentIds: z.array(z.string()),
  description: z.string(),
  specifications: z.string(),
  images: z.array(productAssetSchema),
  installationGuides: z
    .array(productAssetSchema)
    .nullish()
    .transform((v) => v ?? []),
  // By-type-optional: `other` devices carry no subscription and `toDeviceBody` omits both
  // fields for them, so the detail/write payload returns them absent. Tolerate missing/null
  // (coerce to the neutral value) instead of throwing — a throw here renders "Device not
  // found" on the edit page for those devices (story: Products edit-not-found bug).
  subscriptionPrice: z
    .number()
    .nullish()
    .transform((v) => v ?? null),
  notAvailableForPurchase: z
    .boolean()
    .nullish()
    .transform((v) => v ?? false),
  status: deviceStatusSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const DEVICE_NAME_MAX = 100;
const DEVICE_NAME_DISALLOWED = /[^A-Za-z0-9 -]/g;

/** Live input mask: strip anything but [alphanumeric, space, hyphen], cap at 100 (story #102081). */
export function maskDeviceName(raw: string): string {
  return raw.replace(DEVICE_NAME_DISALLOWED, '').slice(0, DEVICE_NAME_MAX);
}

const uploadedFileSchema = z.object({
  id: z.string(),
  url: z.string(),
  name: z.string(),
  size: z.number(),
  contentType: z.string(),
});

/**
 * Create-form values. Numbers are required via `z.number({message})` (empty input is
 * `undefined` → fails → Save stays disabled). `warrantyMonths` and `installationPrice`
 * are the optional exceptions (CR 122166). `subscriptionPrice` is nullable (cleared
 * InputNumber → null). Per-type rules (D3) are enforced in `superRefine`.
 */
export const deviceFormSchema = z
  .object({
    name: z.string().min(2, 'Enter a valid name').max(DEVICE_NAME_MAX),
    deviceType: deviceTypeSchema,
    availableCount: z
      .number({ message: 'Required' })
      .int()
      .min(0)
      .max(AVAILABLE_COUNT_MAX, `Must be ${AVAILABLE_COUNT_MAX.toLocaleString('en-US')} or less`),
    categoryId: z.string().min(1, 'Required'),
    // Optional (CR 122166). Blank is undefined on a fresh form and null once cleared; both send null.
    warrantyMonths: z.number().int().min(0).nullish(),
    price: z.number({ message: 'Required' }).gt(0, 'Price must be greater than 0'),
    installationPrice: z.number().gt(0, 'Price must be greater than 0').nullish(),
    subscriptionPrice: z.number().nullable(),
    componentIds: z.array(z.string()),
    notAvailableForPurchase: z.boolean(),
    description: z.string().min(1, 'Required').max(2000),
    specifications: z.string().min(1, 'Required').max(2000),
    images: z.array(uploadedFileSchema).min(1, 'Add at least one image'),
    installationGuide: z
      .array(uploadedFileSchema)
      .min(1, 'Add at least one installation guide file'),
  })
  .superRefine((v, ctx) => {
    // Subscription Price: required >0 for DM; optional for CropX (>0 if entered); N/A for Other.
    if (v.deviceType === 'dm_device') {
      if (v.subscriptionPrice == null) {
        ctx.addIssue({ code: 'custom', path: ['subscriptionPrice'], message: 'Required' });
      } else if (v.subscriptionPrice <= 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['subscriptionPrice'],
          message: 'Price must be greater than 0',
        });
      }
    } else if (
      v.deviceType === 'cropx' &&
      v.subscriptionPrice != null &&
      v.subscriptionPrice <= 0
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['subscriptionPrice'],
        message: 'Price must be greater than 0',
      });
    }
    // Applicable Components: required ≥1 for DM + CropX; optional for Other.
    if ((v.deviceType === 'dm_device' || v.deviceType === 'cropx') && v.componentIds.length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['componentIds'],
        message: 'Select at least one component',
      });
    }
  });

/** Map validated form values → the create/update body. Uploads become asset-id arrays
 *  (`imageAssetIds`/`installationGuideAssetIds`); ids are stable so create and update send the
 *  same shape. Omits subscription/bundle for `other` (D3). */
export function toDeviceBody(values: z.infer<typeof deviceFormSchema>): {
  name: string;
  deviceType: z.infer<typeof deviceTypeSchema>;
  availableCount: number;
  categoryId: string;
  warrantyMonths: number | null;
  price: number;
  installationPrice: number | null;
  componentIds: string[];
  description: string;
  specifications: string;
  imageAssetIds: string[];
  installationGuideAssetIds: string[];
  subscriptionPrice?: number;
  notAvailableForPurchase?: boolean;
} {
  const isOther = values.deviceType === 'other';
  return {
    name: values.name,
    deviceType: values.deviceType,
    availableCount: values.availableCount,
    categoryId: values.categoryId,
    warrantyMonths: values.warrantyMonths ?? null,
    price: values.price,
    installationPrice: values.installationPrice ?? null,
    componentIds: values.componentIds,
    description: values.description,
    specifications: values.specifications,
    imageAssetIds: values.images.map((f) => f.id),
    installationGuideAssetIds: values.installationGuide.map((f) => f.id),
    subscriptionPrice: isOther ? undefined : (values.subscriptionPrice ?? undefined),
    notAvailableForPurchase: isOther ? undefined : values.notAvailableForPurchase,
  };
}

/** Inverse of toDeviceBody: a detail record → prefilled edit-form values. Upload zones seed from
 *  the detail's `images` / `installationGuides` asset objects (assetId + real url).
 *  `deviceType` is carried into form state (it drives the conditional matrix) even
 *  though the edit form renders no type control. */
export function toDeviceFormValues(
  device: z.infer<typeof deviceSchema>,
): z.infer<typeof deviceFormSchema> {
  return {
    name: device.name,
    deviceType: device.deviceType,
    availableCount: device.availableCount,
    categoryId: device.categoryId,
    warrantyMonths: device.warrantyMonths,
    price: device.price,
    installationPrice: device.installationPrice,
    subscriptionPrice: device.subscriptionPrice,
    componentIds: device.componentIds,
    notAvailableForPurchase: device.notAvailableForPurchase,
    description: device.description,
    specifications: device.specifications,
    images: device.images.map(assetToUploadedFile),
    installationGuide: device.installationGuides.map(assetToUploadedFile),
  };
}
